import { ApolloError } from '@apollo/client';

import { NO_VALID_PASSWORD_PROVIDED } from './checkGraphqlError';
import { addAction, addError } from './logger';

type JoinEventErrorContext = Readonly<{
  context: string;
  operation: string;
  eventId: string;
}>;

/** What browsers reject `fetch` with when the request never got a response (Chrome/Edge, Safari, Firefox). */
const NETWORK_FAILURE_MESSAGES = new Set(['Failed to fetch', 'Load failed', 'NetworkError when attempting to fetch resource.']);

const MAX_REPORTED_MESSAGES = 3;
const MAX_MESSAGE_LENGTH = 120;

const isPasswordRejection = (error: unknown): boolean =>
  error instanceof ApolloError && !error.networkError && error.graphQLErrors.length > 0 && error.graphQLErrors.every(({ message }) => message === NO_VALID_PASSWORD_PROVIDED);

const isNetworkFailure = (error: unknown): boolean => {
  if (error instanceof ApolloError) return !!error.networkError && error.graphQLErrors.length === 0;
  return error instanceof Error && NETWORK_FAILURE_MESSAGES.has(error.message);
};

const truncate = (message: string): string => (message.length > MAX_MESSAGE_LENGTH ? `${message.slice(0, MAX_MESSAGE_LENGTH)}…` : message);

const describeApolloError = (error: ApolloError) => {
  const codes = error.graphQLErrors.map(({ extensions }) => extensions?.['code']).filter((code): code is string => typeof code === 'string');
  const statusCode = (error.networkError as { statusCode?: unknown } | null)?.statusCode;
  return {
    graphQLCodes: codes.length ? codes.join(',') : undefined,
    graphQLMessages: error.graphQLErrors.length
      ? error.graphQLErrors
          .slice(0, MAX_REPORTED_MESSAGES)
          .map(({ message }) => truncate(message))
          .join(' | ')
      : undefined,
    networkStatus: typeof statusCode === 'number' ? statusCode : undefined,
  };
};

/**
 * A wrong or missing event password is the expected answer on a locked site, and a dropped connection is the
 * guest's network, so neither is reported as an error.
 */
export const reportJoinEventError = (error: unknown, context: JoinEventErrorContext): void => {
  if (isPasswordRejection(error)) {
    addAction('EventPasswordRejected', context);
    return;
  }
  if (isNetworkFailure(error)) {
    addAction('EventJoinNetworkFailed', context);
    return;
  }
  addError(error, error instanceof ApolloError ? { ...context, ...describeApolloError(error) } : context);
};
